import React, {
  useState,
  useRef,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Popover,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import ThumbsUpIcon from '../../svg/ThumbsUpIcon';
import ThumbsDownIcon from '../../svg/ThumbsDownIcon';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { MessageDataType } from './messages.types';
import VoiceChatToggle from '../../component/voiceChatToggle';
import AudioChunkQueue from './audioChunkQueue';
import { VolumeContext } from '../../context/VolumeContext';
import useAnalyticsEventTracker from '../../hooks/useAnalyticsEventTracker';
import { preprocessTextForVoice } from '../../utils/helper';
import SubscribeButton from '../Purchases/subscribeButton';
import { useAuth } from '../../hooks/useAuth';
import PremiumPurchaseButton from '../Purchases/premiumPurchaseButton';
import safarivoice from '../../assets/images/Safarivoicefix.png';
import Lottie from 'lottie-react';
import thinkingAnimation from './thinkingAnimation.json';
import { useTheme } from '@mui/material/styles';
import VolumeMeter from './VolumeMeter';
import ScrollingTextField from './ScrollingTextField';
import { motion } from 'framer-motion';
import { isSafari, requiresPushToStart } from '../../utils/browser';
import useSTT from './useSTT';
// import useWebSpeechSTT from './useWebSpeechSTT';
// import useGoogleSTT from './useGoogleSTT';

interface VoiceChatProps {
  handleMessageSend: (
    userMessage: string,
    firstMessage: boolean,
    isVoiceMessage: boolean
  ) => Promise<any>;
  creatorProfileData: any;
  handleLikeDislikeMessage: (msgId: string, value: number) => void;
  messageDataItem?: MessageDataType;
  toggleVoiceChat: (newSetting: boolean) => void;
  useVoiceChat: boolean;
  setErrorCode: React.Dispatch<React.SetStateAction<number>>;
  setShowTimeoutModal: React.Dispatch<React.SetStateAction<boolean>>;
  showCheckPermissionsModal: () => void;
  isWidget?: boolean;
  showTranscript?: boolean;
  ignoreCredits?: boolean;
  showAssignmentButton?: boolean;
  handleStartAssignment?: () => void;
  isAwaitingMessage?: boolean;
  setIsAwaitingMessage?: React.Dispatch<React.SetStateAction<boolean>>;
  shouldPlayMessage?: boolean;
  shouldStopAudio?: boolean;
  setShouldStopAudio?: React.Dispatch<React.SetStateAction<boolean>>;
  setShouldPlayMessage?: React.Dispatch<React.SetStateAction<boolean>>;
  requiredPremiumKey?: string;
  premiumAccess?: boolean;
  roomId?: string;
}

const enableMonetization = process.env.REACT_APP_ENABLE_MONETIZATION === 'true';
const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

const VoiceChat: React.FC<VoiceChatProps> = (props) => {
  const {
    handleMessageSend,
    creatorProfileData,
    handleLikeDislikeMessage,
    messageDataItem,
    toggleVoiceChat,
    useVoiceChat,
    setErrorCode,
    setShowTimeoutModal,
    showCheckPermissionsModal,
    isWidget = false,
    ignoreCredits,
    showAssignmentButton,
    handleStartAssignment,
    isAwaitingMessage,
    setIsAwaitingMessage,
    shouldPlayMessage,
    setShouldPlayMessage,
    shouldStopAudio = false,
    setShouldStopAudio,
    requiredPremiumKey,
    premiumAccess,
    roomId,
  } = props;

  const msgId = messageDataItem?.msgId;
  const likeDislike = messageDataItem?.likeDislike;
  const message = messageDataItem?.editedMessage || messageDataItem?.message;
  const eventTracker = useAnalyticsEventTracker();

  const {
    transcript,
    interimTranscript,
    listening,
    connecting,
    start,
    stop,
    clear,
    micAvailable,
    micPermissionAllowed,
    errorMsg,
    isAudioActive,
  } = useSTT();

  const [disableMicrophone, setDisableMicrophone] = useState(false);
  const [, forceRenderIsPlayingAudio] = useState(false);
  const [forceRefresh, setForceRefresh] = useState<boolean>(false);
  const [openHelpPopup, setOpenHelpPopup] = useState(false);
  const [animatePersonaTranscript, setAnimatePersonaTranscript] = useState(false);
  const [activeChunkText, setActiveChunkText] = useState<string>('');
  const [messageChunks, setMessageChunks] = useState<string[]>([]);
  const [animateUserTranscript, setAnimateUserTranscript] = useState(false);
  const [firstDivHeight, setFirstDivHeight] = useState(0);
  const [visibleWelcomeMsg, setVisibleWelcomeMsg] = useState(false);
  const [userTransmitted, setUserTransmitted] = useState(false);

  const listeningStartTime = useRef(0);
  const avatarCanvas = useRef(null);
  const microphoneCanvas = useRef(null);
  const micButton = useRef(null);
  const isPlayingAudio = useRef(false);
  const awaitingResp = useRef(false);
  const awaitingRespStartTime = useRef(Date.now());
  const audioAnalyzer = useRef(null);
  const volumeControl = useRef(null);
  const audioQueue = useRef(null);
  const emptyTranscriptCount = useRef(0);
  const parentRef = useRef(null);
  // const hasMounted = useRef(false);
  const firstMotionDivRef = useRef(null);
  const pauseTimeout = useRef<NodeJS.Timeout | null>(null);

  const setAvatarCanvas = useCallback((val: any) => {
    resizeCanvases();
    avatarCanvas.current = val;
  }, []);

  const setMicButton = useCallback((val: any) => {
    micButton.current = val;
  }, []);

  const setIsPlayingAudio = useCallback((val: boolean) => {
    requestAnimationFrame(speakingAnimation);
    isPlayingAudio.current = val;
    if (val) {
      setAnimatePersonaTranscript(true);
    }
    forceRenderIsPlayingAudio((prev) => !prev);
  }, []);

  const { volume } = useContext(VolumeContext);
  const { authUser, isAnonymousUser } = useAuth();
  const theme = useTheme();

  const isSelfChat = authUser?.documentId === creatorProfileData?.userId;
  const isDarkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    if (firstMotionDivRef.current) {
      setFirstDivHeight(firstMotionDivRef.current.offsetHeight);
    }
  }, [transcript, animateUserTranscript]);

  useEffect(() => {
    setVisibleWelcomeMsg(true);
    const timer = setTimeout(() => setVisibleWelcomeMsg(false), 4000);

    // Safari and Android Chrome require user interaction before starting speech recognition
    if (requiresPushToStart()) {
      // Don't auto-start for browsers that need push-to-start, wait for user gesture
      if (enableDevTools) {
        console.log('Push-to-start browser detected: waiting for user gesture to start speech recognition');
      }
    } else {
      startListening();
    }

    return () => {
      clearTimeout(timer);
      stop();
      if (setShouldStopAudio) setShouldStopAudio(false);
      stopAudio();
    };
  }, []);

  useEffect(() => {
    if (errorMsg) {
      // You can implement a more sophisticated error display, e.g., a toast notification
      console.error(errorMsg);
      if (!micPermissionAllowed) {
        showCheckPermissionsModal();
      }
    }
  }, [errorMsg, micPermissionAllowed, showCheckPermissionsModal]);

  const handleOpenHelpPopup = () => {
    setOpenHelpPopup(true);
  };

  const handleCloseHelpPopup = () => {
    setOpenHelpPopup(false);
  };

  const handleChunkChange = useCallback((chunkText: string) => {
    setActiveChunkText(chunkText);
  }, []);

  useEffect(() => {
    if (micAvailable) {
      window.addEventListener('resize', resizeCanvases, false);
      resizeCanvases();
      return function cleanup() {
        window.removeEventListener('resize', resizeCanvases, false);
        awaitingResp.current = false;
        setIsPlayingAudio(false);
        setDisableMicrophone(false);
        stopAudio();
      };
    } else {
      toggleVoiceChat(false);
    }
  }, [micAvailable]);

  useEffect(() => {
    if (messageDataItem === undefined && !listening) {
      handleStopListening(true);
    }
  }, [messageDataItem, listening]);

  const startListening = () => {
    if (!listening && !connecting && useVoiceChat) {
      clear();
      start();
      listeningStartTime.current = Date.now();
      setAnimateUserTranscript(false);
      setAnimatePersonaTranscript(false);
      setUserTransmitted(false);
      requestAnimationFrame(listeningAnimation);
    }
  };

  const handleStopListening = async (isInitial?: boolean) => {
    if (pauseTimeout.current) {
      clearTimeout(pauseTimeout.current);
      pauseTimeout.current = null;
    }

    stop();

    if (transcript.trim().length > 0 && !userTransmitted) {
      setUserTransmitted(true);
      emptyTranscriptCount.current = 0;
      awaitingResp.current = true;
      setAnimateUserTranscript(true);
      awaitingRespStartTime.current = Date.now();
      setDisableMicrophone(true);
      resizeCanvases();
      try {
        let msg = await handleMessageSend(transcript, !!isInitial, true);
        chunkAndGetTTS(msg);
      } catch (err) {
        if (enableDevTools) console.log(err);
        awaitingResp.current = false;
        setDisableMicrophone(false);
        startListening();
      }
    } else {
      if (!isInitial) {
        emptyTranscriptCount.current++;
        if (emptyTranscriptCount.current >= 3) {
          showCheckPermissionsModal();
        }
      }
    }
  };

  useEffect(() => {
    const clearTimer = () => {
      if (pauseTimeout.current) {
        clearTimeout(pauseTimeout.current);
        pauseTimeout.current = null;
      }
    };
    if (listening) {
      clearTimer();
      pauseTimeout.current = setTimeout(() => {
        if (!isAudioActive) { // only stop when audio is not actively being recorded
          handleStopListening();
        }
      }, 4000);
    } else {
      clearTimer();
    }
    return clearTimer;
  }, [interimTranscript, transcript, listening, handleStopListening, isAudioActive]);

  const stopAudio = () => {
    audioQueue.current?.forceStop();
  };

  useEffect(() => {
    if (isAwaitingMessage) {
      awaitingResp.current = true;
      setAnimateUserTranscript(true);
      setIsAwaitingMessage(false);
    }
  }, [isAwaitingMessage]);

  useEffect(() => {
    if (shouldPlayMessage) {
      handlePlayMessage();
      setShouldPlayMessage(false);
    }
  }, [shouldPlayMessage]);

  useEffect(() => {
    if (shouldStopAudio) {
      if (setShouldStopAudio) setShouldStopAudio(false);
      stopAudio();
    }
  }, [shouldStopAudio]);

  const handlePlayMessage = async () => {
    awaitingResp.current = true;
    setAnimateUserTranscript(true);
    awaitingRespStartTime.current = Date.now();
    setDisableMicrophone(true);
    resizeCanvases();
    chunkAndGetTTS(message);
  };

  function chunkAndGetTTS(message: string) {
    if (message) {
      try {
        let msg = preprocessTextForVoice(message);
        let sentences = msg.split('. ');
        let chunks = [];
        if (sentences.length) chunks.push(sentences[0]);
        if (sentences.length > 1) chunks.push(sentences[1]);
        for (let i = 2; i < sentences.length; i++) {
          let str = sentences[i];
          if (chunks[chunks.length - 1].length + str.length + 2 < 500) {
            chunks[chunks.length - 1] += '. ' + str;
          } else {
            chunks.push(str);
          }
        }
        setMessageChunks(chunks);
        audioQueue.current = new AudioChunkQueue(
          chunks,
          creatorProfileData.voiceId,
          awaitingResp,
          volumeControl,
          audioAnalyzer,
          setDisableMicrophone,
          setIsPlayingAudio,
          volume.current,
          setErrorCode,
          setShowTimeoutModal,
          startListening,
          handleChunkChange
        );
        eventTracker('voice-chat');
      } catch (e) {
        eventTracker('voice-chat failed');
        throw e;
      }
    } else {
      awaitingResp.current = false;
      setDisableMicrophone(false);
      startListening();
    }
  }

  function resizeCanvases() {
    const microphoneBox = document.getElementById('microphone-canvas-box');
    const avatarCanvasBox = document.getElementById('avatar-canvas-box');
    if (microphoneCanvas.current) {
      microphoneCanvas.current.width = microphoneBox.clientWidth;
      microphoneCanvas.current.height = microphoneBox.clientHeight;
      requestAnimationFrame(listeningAnimation);
    }
    if (avatarCanvas.current) {
      avatarCanvas.current.width = avatarCanvasBox.clientWidth;
      avatarCanvas.current.height = avatarCanvasBox.clientHeight;
    }
    setForceRefresh(!forceRefresh);
  }

  const animateConcentricCircles = (ctx, center, innerRadius, outerRadius) => {
    const [x, y] = center;
    const gradient = ctx.createRadialGradient(
      x,
      y,
      innerRadius,
      x,
      y,
      outerRadius
    );
    gradient.addColorStop(0, theme.palette.background['container-brand-2']); // Inner color
    gradient.addColorStop(1, theme.palette.background['container-brand-4']); // Outer color
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, outerRadius + 5, 0, 2 * Math.PI);
    ctx.fill();
  };

  const listeningAnimation = () => {
    const canvas = microphoneCanvas.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!listening) {
        ctx.fillStyle = '#8ad5f8';
        ctx.fillRect(0, 32 + 32, canvas.width, canvas.height - 32 - 32);
      } else {
        let maxAmp = 32 + 32;
        let startY = 32 + 32;
        let amp, freq, phase;
        ctx.fillStyle = '#3a9de0';
        amp =
          (maxAmp / 4) *
          Math.sin(
            (0.96 * (Date.now() - awaitingRespStartTime.current)) / 1000
          );
        freq = ((2 * Math.PI) / canvas.width) * 2.78;
        phase =
          2 *
          Math.PI *
          (((0.7 * (Date.now() - awaitingRespStartTime.current)) / 1000) % 1);
        fillWave(canvas, ctx, startY, amp, freq, phase);
        ctx.fillStyle = '#3EACEF';
        amp =
          (maxAmp / 2.5) *
          Math.sin(
            (1.13 * (Date.now() - awaitingRespStartTime.current)) / 1000
          );
        freq = ((2 * Math.PI) / canvas.width) * 3.14;
        phase =
          2 *
          Math.PI *
          (((-0.4 * (Date.now() - awaitingRespStartTime.current)) / 1000) % 1);
        fillWave(canvas, ctx, startY, amp, freq, phase);
        ctx.fillStyle = '#8ad5f8';
        amp =
          (maxAmp / 3) *
          Math.sin((3 * (Date.now() - awaitingRespStartTime.current)) / 1000);
        freq = ((2 * Math.PI) / canvas.width) * 2;
        phase =
          2 *
          Math.PI *
          (((0.38 * (Date.now() - awaitingRespStartTime.current)) / 1000) % 1);
        fillWave(canvas, ctx, startY, amp, freq, phase);
        requestAnimationFrame(listeningAnimation);
      }
    }
  };

  const fillWave = (canvas, ctx, startY, amplitude, freq, phase) => {
    ctx.beginPath();
    ctx.moveTo(0, startY);
    for (let x = 0; x < canvas.width; x++) {
      let y = amplitude * Math.sin(freq * x + phase);
      ctx.lineTo(x, y + startY);
    }
    ctx.lineTo(canvas.width, canvas.height);
    ctx.lineTo(0, canvas.height);
    ctx.lineTo(0, startY);
    ctx.fill();
  };

  const speakingAnimation = () => {
    const canvas = avatarCanvas.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (isPlayingAudio.current) {
        let curTime = Date.now();
        let center = [canvas.width / 2, canvas.height / 2];
        let innerRadius = canvas.width / 2 / 1.5 + 5;
        let freq = 0.5;
        let maxChange = canvas.width / 2 - innerRadius;
        let outerRadius: any;
        if (audioAnalyzer.current) {
          const bufferLength = audioAnalyzer.current.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);
          audioAnalyzer.current.getByteFrequencyData(dataArray);
          let sum = 0;
          dataArray.forEach((el) => (sum += el * el));
          const volume = Math.sqrt(sum / dataArray.length);
          outerRadius = innerRadius + (maxChange * volume) / 80;
        } else {
          outerRadius =
            innerRadius +
            (maxChange / 2) *
              (1 -
                Math.cos(
                  (2 *
                    Math.PI *
                    freq *
                    (curTime - listeningStartTime.current)) /
                    1000
                ));
        }
        animateConcentricCircles(ctx, center, innerRadius, outerRadius);
        requestAnimationFrame(speakingAnimation);
      }
    }
  };

  if (!micAvailable && !micPermissionAllowed) {
    return (
      <span>
        Microphone not available or permission denied. Please check your
        settings.
      </span>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'stretch',
        height: '100%',
        width: '100%',
        marginTop: isWidget ? '20px' : '0',
        maxWidth: '1080px',
        margin: '0 auto',
        px: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexGrow: 1,
          width: '100%',
          maxHeight: 'calc(100% - 168px)',
          overflowX: 'hidden',
        }}
      >
        <Box
          sx={{
            width: isWidget
              ? '30vh'
              : { xs: '18vh', sx: '20vh', md: '22vh', lg: '22vh', xl: '24vh' },
            height: isWidget
              ? '30vh'
              : { xs: '18vh', sx: '20vh', md: '22vh', lg: '22vh', xl: '24vh' },
            mt: isWidget
              ? '0vh'
              : { xs: '9vh', sx: '10vh', md: '11vh', lg: '11vh', xl: '12vh' },
            mb: isWidget
              ? '0vh'
              : {
                  xs: '4.5vh',
                  sx: '5.5vh',
                  md: '5.5vh',
                  lg: '5.5vh',
                  xl: '6vh',
                },
            position: 'relative',
          }}
        >
          <Box
            id='avatar-canvas-box'
            sx={{
              position: 'absolute',
              width: '150%',
              height: '150%',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              overflow: 'hidden',
            }}
          >
            <canvas id='avatar-canvas' ref={setAvatarCanvas}></canvas>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              width: '130%',
              height: '130%',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                filter: isDarkMode ? 'invert(1)' : 'invert(0)',
                width: '100%',
                height: '100%',
                position: 'absolute',
              }}
            >
              {awaitingResp.current && (
                <Lottie animationData={thinkingAnimation} loop autoPlay />
              )}
            </div>
          </Box>
          <Avatar
            src={creatorProfileData?.profilePicUrl}
            alt={creatorProfileData?.fullName}
            sx={{
              width: '100%',
              height: '100%',
              aspectRatio: 1,
              borderRadius: '40%',
              fontSize: '8.125rem',
              position: 'relative',
            }}
          />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: {
              xs: 'calc(27vh + 24px)',
              sx: 'calc(30vh + 24px)',
              md: 'calc(33vh + 24px)',
              lg: 'calc(33vh + 24px)',
              xl: 'calc(36vh + 24px)',
            },
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
          }}
        >
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: visibleWelcomeMsg ? 1 : 0 }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '3lh',
                border: '1px solid gray',
                borderRadius: '4px',
                padding: 2,
                backgroundColor: 'white',
                width: '100%',
                maxWidth: 'calc(100% - 32px)',
              }}
            >
              <Typography color='text.primary' align='center'>
                Welcome to voice chat! Start speaking to ask me a question.
              </Typography>
            </Box>
          </motion.div>
        </Box>
        {enableMonetization &&
        !isAnonymousUser &&
        !isSelfChat &&
        !isWidget &&
        requiredPremiumKey &&
        !premiumAccess ? (
          <Box
            sx={{
              width: '100%',
              margin: 'auto 0',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {requiredPremiumKey && !premiumAccess ? (
              <PremiumPurchaseButton
                requiredPremiumKey={requiredPremiumKey}
                redirectURL={`/${creatorProfileData.userName}/chat`}
              />
            ) : (
              <SubscribeButton
                redirectURL={`/${creatorProfileData.userName}/chat`}
              />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <motion.div
              ref={firstMotionDivRef}
              initial={{
                position: 'absolute',
                bottom: 0,
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.action.selected,
              }}
              animate={
                animateUserTranscript
                  ? {
                      position: 'absolute',
                      bottom: 'unset',
                      top: 0,
                      color: theme.palette.primary.contrastText,
                      backgroundColor: theme.palette.primary.main,
                    }
                  : {}
              }
              transition={{
                duration: animateUserTranscript ? 1.5 : 0,
                ease: 'easeOut',
              }}
              style={{
                padding: '16px',
                borderRadius: '16px',
                maxWidth: '100%',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                marginBottom: '24px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                direction: 'ltr',
                textAlign: 'left',
              }}
            >
              {transcript || '...'}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 0 }}
              animate={
                animatePersonaTranscript
                  ? {
                      opacity: 1,
                      x: 0,
                      transition: { duration: 0.1, ease: 'easeOut' },
                    }
                  : {
                      opacity: 0,
                      x: -150,
                      transition: { duration: 1.5, ease: 'easeInOut' },
                    }
              }
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                marginTop: `${firstDivHeight + 24}px`,
              }}
            >
              <ScrollingTextField
                chunks={messageChunks}
                activeChunkText={activeChunkText}
                placeholder='Message will appear here...'
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  position: 'relative',
                  visibility:
                    messageDataItem && !awaitingResp.current
                      ? 'visible'
                      : 'hidden',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    id={`button-like-message-${msgId}`}
                    sx={{
                      padding: '0px',
                      margin: '0px 6px 0px 0px',
                      color:
                        likeDislike === 1
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                    }}
                    onClick={() => handleLikeDislikeMessage(msgId, 1)}
                  >
                    <ThumbsUpIcon
                      height='30px'
                      width='30px'
                      fill={
                        likeDislike === 1 ? theme.palette.primary.main : 'none'
                      }
                    />
                  </IconButton>
                  <IconButton
                    id={`button-dislike-message-${msgId}`}
                    sx={{
                      padding: '0px',
                      margin: '0px 0px 0px 7px',
                      color:
                        likeDislike === 2
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                    }}
                    onClick={() => handleLikeDislikeMessage(msgId, 2)}
                  >
                    <ThumbsDownIcon
                      height='30px'
                      width='30px'
                      fill={
                        likeDislike === 2 ? theme.palette.primary.main : 'none'
                      }
                    />
                  </IconButton>
                </Box>
                <Button
                  id={`button-play-message-${msgId}`}
                  variant='contained'
                  color='secondary'
                  size='large'
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                  onClick={handlePlayMessage}
                  endIcon={
                    <PlayArrowIcon
                      sx={{ fill: (theme) => theme.palette.primary.main }}
                    />
                  }
                >
                  Replay
                </Button>
              </Box>
            </motion.div>
          </Box>
        )}
      </Box>
      <Box
        ref={parentRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          width: '100%',
          maxHeight: '128px',
        }}
      >
        {showAssignmentButton && (
          <Popover
            open={showAssignmentButton}
            anchorEl={micButton.current}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'center',
            }}
            slotProps={{
              paper: {
                sx: {
                  background: 'transparent',
                  boxShadow: 'none',
                },
              },
            }}
            sx={{
              zIndex: 1,
            }}
          >
            <Button
              onClick={handleStartAssignment}
              variant='contained'
              sx={{
                mt: 2,
                mb: 2,
                ml: 2,
                mr: 2,
                maxWidth: '200px',
                alignSelf: 'center',
                boxShadow: '0 0 0 12px #C2E8FF',
                '&:hover': {
                  boxShadow: '0 0 0 12px #C2E8FF !important',
                },
              }}
            >
              Start Assignment
            </Button>
          </Popover>
        )}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: '600px',
          }}
        >
          <VoiceChatToggle
            toggleVoiceChat={toggleVoiceChat}
            useVoiceChat={useVoiceChat}
            audioQueue={audioQueue}
          />
        </Box>
        <VolumeMeter isListening={listening} dotSize={4} padding={1} />
        <Box
          sx={{
            position: 'absolute',
            width: '120px',
            height: '120px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bottom: '48px',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <IconButton
            id={`button-microphone`}
            ref={setMicButton}
            sx={{
              width: '70px',
              height: '70px',
              borderRadius: '70px',
              border: `3px solid ${theme.palette.primary.main}`,
              color: (theme) => theme.palette.primary.main,
              backgroundColor: (theme) =>
                theme.palette.background['paper-elevation-2'],
              ':hover': {
                backgroundColor: (theme) =>
                  theme.palette.background['paper-elevation-2'],
                '& svg': {
                  transition: 'all 0.25s',
                },
              },
              '&.Mui-disabled': {
                border: `3px solid ${theme.palette.action.disabled}`,
              },
            }}
            disabled={
              (enableMonetization &&
                !isAnonymousUser &&
                !isWidget &&
                !isSelfChat &&
                !ignoreCredits &&
                requiredPremiumKey &&
                !premiumAccess) ||
              disableMicrophone ||
              showAssignmentButton ||
              connecting
            }
            onPointerDown={() => {
              if (!listening) {
                // Safari and Android Chrome need explicit user gesture to start speech recognition
                if (requiresPushToStart() && !micAvailable) {
                  showCheckPermissionsModal();
                  return;
                }
                startListening();
              } else {
                handleStopListening();
              }
            }}
          >
            {connecting ? (
              <CircularProgress size={45} />
            ) : listening ? (
              <StopIcon
                sx={{
                  height: '45px',
                  width: '45px',
                  color: (theme) => theme.palette.primary.main,
                }}
              />
            ) : (
              <MicIcon
                sx={{
                  height: '45px',
                  width: '45px',
                  color: disableMicrophone
                    ? (theme) => theme.palette.action.disabled
                    : (theme) => theme.palette.primary.main,
                }}
              />
            )}
          </IconButton>
        </Box>
        {
          <Box
            sx={{
              position: 'absolute',
              bottom: '12px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100%',
              maxWidth: '600px',
            }}
          >
            <Typography
              sx={{
                width: '100%',
                alignItems: 'center',
                position: 'relative',
                textAlign: 'center',
                color: (theme) => theme.palette.primary.main,
              }}
            >
              {connecting
                ? 'Connecting'
                : disableMicrophone || showAssignmentButton
                ? ' '
                : listening
                ? 'Listening...'
                : 'Click to start speaking...'}
            </Typography>
          </Box>
        }
        {isSafari() && (
          <Box
            sx={{
              position: 'absolute',
              right: '20px',
              top: '10px',
              cursor: 'pointer',
            }}
            onClick={handleOpenHelpPopup}
          >
            <Typography variant='body2' color='primary'>
              Voice Not Working?
            </Typography>
          </Box>
        )}
        <Dialog open={openHelpPopup} onClose={handleCloseHelpPopup}>
          <DialogContent>
            <Typography
              variant='h5'
              gutterBottom
              align='center'
              sx={{ fontWeight: 'bold' }}
            >
              Having Trouble With Voice?
            </Typography>
            <Typography
              align='center'
              sx={{ color: 'black', fontSize: '1.2rem' }}
            >
              You may need to adjust your Safari settings and grant microphone permissions!
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <img
                src={safarivoice}
                alt='Safari Voice Settings Help'
                style={{ maxWidth: '100%' }}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'center',
              mt: -3,
              paddingBottom: '20px',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Button
                onClick={handleCloseHelpPopup}
                variant='outlined'
                sx={{
                  color: '#009CDB !important',
                  border: '1px solid #009CDB !important',
                  width: '50%',
                  maxHeight: '36px',
                }}
              >
                I'm not having any problems
              </Button>
              <Button
                onClick={() => window.open('/faq')}
                variant='contained'
                sx={{ width: '50%', maxHeight: '36px' }}
              >
                Go to FAQs
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default VoiceChat;
